import React from 'react';
import { Box } from '@mui/material';

interface ClinicalImageTileProps {
    /**
     * The object URL of an image that decrypted successfully. The tile never fetches,
     * never holds a key, never decrypts — and never renders a failure, because there is
     * no failure it could be handed: each surface answers "did the bytes arrive?" above
     * this component, where it also answers "may this reviewer see them?".
     */
    blobUrl: string;
    /**
     * Width of the slot: px for a fixed square, `'100%'` to fill the container.
     *
     * Both dimensions are required and independent. They started out as one `size` plus a
     * `fullWidth` flag, which read as a square that could be stretched — but `size` then
     * meant "both axes" for one caller and "height only" for the other, so its documented
     * meaning was false on half the call sites, and two of the four combinations the flags
     * could express were not slots anyone wanted.
     */
    width: number | '100%';
    /** Height of the slot in px. */
    height: number;
    /** Draw its own border and radius. Off inside a card that already draws one. */
    framed?: boolean;
    /** Open the enlarged view. Omitted where the surface offers no enlarge. */
    onEnlarge?: () => void;
}

/**
 * SKIN-281 — the single place that decides how a decrypted clinical photograph is drawn.
 *
 * The clinical record (`EncryptedImage`) and the Quality Check gallery (`ImageReviewCard`)
 * each drew their own `<img>` — same `objectFit`, same click-to-enlarge — so a fix to how a
 * clinical photograph renders reached only the surface it was made on, and nothing failed
 * when it was made once.
 *
 * That `<img>` is the whole of what was duplicated, and therefore the whole of what this
 * component owns. The failure rendering was **already** shared: `UnavailableImage` has
 * served both surfaces since SKIN-206. Pulling it in here would mean each caller building a
 * union describing an outcome it already knows, so this component could take it apart again
 * and hand the same four props to the component the caller could have called directly —
 * indirection bought with a shared type, in exchange for nothing. So both surfaces keep a
 * symmetric early return above the tile, and the tile is only ever handed pixels that exist.
 *
 * It takes a resolved `blobUrl` rather than a localId or a key, which is what lets it serve
 * two different decrypt orchestrations without knowing either exists — the clinical record
 * decrypts a batch behind one spinner, QC decrypts per image so a 21-image visit fills in
 * card by card. It is also why nothing security-relevant lives here: the crypto is shared
 * through `decryptFileFromS3` and `decodeClinicalImageToBlob`, and this component sees only
 * the object URL that comes out the far end.
 *
 * **What it must not grow.** Each surface keeps its own metadata row above the tile — the
 * clinical record colours the quality score at 70/40 and prints the identifiable flag as
 * two states; QC shows the score uncoloured, because judging an auto-QC known to mark good
 * photographs badly is the point of the feature, and treats the flag as three states,
 * because `null` means the device recorded no opinion and must never read as an assurance
 * about a patient's photograph. Those are product decisions that happen to look like
 * duplication. Pulling either row in here would quietly move one surface onto the other's
 * answer.
 *
 * QC's curtain likewise stays in the card, evaluated *before* this tile is reached: a
 * withheld image would otherwise be drawn as a failure, telling a reviewer a photograph is
 * broken when it was simply not shown to them.
 */
export default function ClinicalImageTile({
    blobUrl,
    width,
    height,
    framed = false,
    onEnlarge,
}: ClinicalImageTileProps) {
    return (
        <Box
            component="img"
            src={blobUrl}
            alt=""
            data-testid="image-thumbnail"
            onClick={onEnlarge}
            sx={{
                width,
                height,
                objectFit: 'cover',
                cursor: onEnlarge ? 'pointer' : 'default',
                ...(framed && {
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                }),
            }}
        />
    );
}
